'use client'

import { formatCurrency } from '@/lib/utils'
import { getStockStatus } from '@/utils/get-stock-badge'
import {
    CardTitleLink,
    CompactCard,
    EmptyResult,
    MoreRow,
    PillLink,
    ResultBlock,
    Row,
    RowList,
    RowMeta,
    RowTitle,
    StatusPill,
} from './primitives'

export type PartRow = {
    id: string
    name: string
    category?: string | null
    description?: string | null
    quantity?: number | string | null
    price?: string | null
    supplier?: string | null
    size?: string | null
    reorderLevel?: number | string | null
    notes?: string | null
}

export const partHref = (id: string) => `/parts?selected=${id}`

const STOCK_TONE = {
    'Out of Stock': 'destructive',
    'Low Stock': 'warning',
    'In Stock': 'success',
} as const

/**
 * Stock pill driven by the same `getStockStatus` the dashboard uses, so chat and
 * dashboard never disagree about whether a part is low.
 */
function StockPill({ part }: { part: PartRow }) {
    if (part.quantity === null || part.quantity === undefined) return null
    const status = getStockStatus(part.quantity, part.reorderLevel ?? null)
    return <StatusPill tone={STOCK_TONE[status]}>{status}</StatusPill>
}

function partMeta(part: PartRow): string {
    const qty =
        part.quantity === null || part.quantity === undefined ? null : `Qty ${part.quantity}`
    return [qty, part.size, part.category, part.supplier]
        .filter((v): v is string => Boolean(v))
        .join(' · ')
}

export function PartResultList({
    parts,
    totalCount,
}: {
    parts: PartRow[]
    totalCount?: number
}) {
    if (parts.length === 0) {
        return <EmptyResult>No matching parts or supplies.</EmptyResult>
    }

    const total = totalCount ?? parts.length
    const label = total === 1 ? '1 part' : `${total} parts`

    return (
        <ResultBlock label={label}>
            <RowList>
                {parts.map((part) => (
                    <Row key={part.id} href={partHref(part.id)}>
                        <div className="min-w-0 flex-1">
                            <RowTitle>{part.name}</RowTitle>
                            <RowMeta>{partMeta(part)}</RowMeta>
                        </div>
                        <StockPill part={part} />
                    </Row>
                ))}
                <MoreRow count={total - parts.length} href="/parts" />
            </RowList>
        </ResultBlock>
    )
}

export function PartDetailCard({
    part,
    linkedItems = [],
}: {
    part: PartRow
    linkedItems?: { id: string; name: string }[]
}) {
    const price = part.price ? Number(part.price) : null

    return (
        <ResultBlock label={part.category ?? 'Part'}>
            <CompactCard className="flex flex-col gap-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <CardTitleLink href={partHref(part.id)}>{part.name}</CardTitleLink>
                        <RowMeta>{partMeta(part)}</RowMeta>
                    </div>
                    <StockPill part={part} />
                </div>

                {part.description && (
                    <p className="text-sm leading-relaxed text-muted-foreground">
                        {part.description}
                    </p>
                )}

                {price !== null && !Number.isNaN(price) && (
                    <div>
                        <p className="text-xs text-muted-foreground">Price</p>
                        <p className="text-sm font-medium tabular-nums text-foreground">
                            {formatCurrency(price)}
                        </p>
                    </div>
                )}

                {linkedItems.length > 0 && (
                    <div className="flex flex-col gap-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Used by
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {linkedItems.map((item) => (
                                <PillLink key={item.id} href={`/items?selected=${item.id}`}>
                                    {item.name}
                                </PillLink>
                            ))}
                        </div>
                    </div>
                )}
            </CompactCard>
        </ResultBlock>
    )
}
